import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useAppDispatch } from "../../store";
import { getRecording } from "../../api/projects";
import { IMAGES_PATH } from "../../constants";
import { Box, Typography, Button, AppBar, Toolbar } from "@mui/material";
import FlexBox from "../../components/layouts/Flexbox";
import ZoomPlayer from "../../components/ZoomPlayer";
import { Error } from "@mui/icons-material";
import { LoadingSpinner } from "../../components/loading-spinners";
import { useRecordingViewerStyles } from "./style";
import Logo from "../../components/Logo";

export default function RecordingViewer() {
  const params = useParams();
  const classes = useRecordingViewerStyles();
  const dispatch = useAppDispatch();
  const token = params.token;
  const [recordingDetails, setRecordingDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    fetchRecording(token);
  }, [token]);

  const fetchRecording = async (token: string) => {
    try {
      setIsError(false);
      setErrorMessage("");
      setIsLoading(true);
      const response = await dispatch(getRecording(token));
      if (response?.success) {
        setRecordingDetails(response.data);
      } else {
        setIsError(true);
        setErrorMessage(
          "We couldn’t fetch this recording right now. Please try again."
        );
      }
    } catch (e) {
      setIsError(true);
      setErrorMessage("Something went wrong while fetching the recording.");
    } finally {
      setIsLoading(false);
    }
  };

  const zoomRecordingUrl = useMemo(
    () => recordingDetails?.zoomRecordingDetails?.recordings || "",
    [recordingDetails]
  );
  const downloadUrlsMap = useMemo(
    () => recordingDetails?.zoomRecordingDetails?.downloadUrls || {},
    [recordingDetails]
  );
  const transcriptKey = useMemo(
    () =>
      Object.keys(downloadUrlsMap).find((k) => k.includes("audio_transcript")),
    [downloadUrlsMap]
  );
  const transcriptDownloadUrl = useMemo(
    () => (transcriptKey ? downloadUrlsMap[transcriptKey] : ""),
    [transcriptKey, downloadUrlsMap]
  );
  const userData = useMemo(
    () => ({
      ambassador: recordingDetails?.ambassador,
      client: recordingDetails?.client,
    }),
    [recordingDetails]
  );

  const renderBody = () => {
    if (isLoading) {
      return <LoadingSpinner loading={isLoading} />;
    }

    if (isError) {
      return (
        <FlexBox className={classes.errorWrapper}>
          <Box className={classes.errorCard}>
            <img
              src={`${IMAGES_PATH}/something-went-wrong.jpg`}
              alt="Error"
              className={classes.errorImage}
            />
            <Typography variant="h6" gutterBottom>
              Unable to load recording
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {errorMessage || "Please check your connection and try again."}
            </Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={() => fetchRecording(token as string)}
            >
              Retry
            </Button>
          </Box>
        </FlexBox>
      );
    }

    if (!zoomRecordingUrl) {
      return (
        <FlexBox className={classes.emptyWrapper}>
          <Error fontSize="medium" color="error" />
          <Typography component={"span"} color={"error"} pt={0.5}>
            Failed to load the recording. Please try again later.
          </Typography>
        </FlexBox>
      );
    }

    const player = zoomRecordingUrl.includes("zoom.us") ? (
      <iframe
        src={zoomRecordingUrl}
        title="Zoom Recording"
        className={classes.iframe}
        allow="autoplay; fullscreen; picture-in-picture"
      />
    ) : (
      <ZoomPlayer
        title={getTitle(userData)}
        videoUrl={zoomRecordingUrl}
        vttUrl={transcriptDownloadUrl}
        downloadUrls={downloadUrlsMap}
        summaryData={recordingDetails?.meetingSummary}
      />
    );

    return (
      <Box className={classes.playerCard}>
        <Box className={classes.playerInner}>{player}</Box>
      </Box>
    );
  };

  return (
    <Box className={classes.container}>
      <AppBar className={classes.header} position="static">
        <Toolbar>
          <Box className={classes.headerContent}>
            <Logo />
          </Box>
        </Toolbar>
      </AppBar>
      <Box className={classes.pageInner}>{renderBody()}</Box>
    </Box>
  );
}

function getTitle(userData) {
  return `Virtual Meeting (Facilitated by Execweb) - ${userData?.ambassador?.firstName}, ${userData?.ambassador?.title}  @ ${userData?.ambassador?.company}`;
}
